import React, { useContext, useState } from 'react';
import { TabsView } from '@fiverr-private/personalized_content';
import { Box } from '@fiverr-private/theme';
import {
    getBuyerState,
    getMixPanelCommonProperties,
    IClickData,
    sendClickEvent,
    onCardImpression,
} from '@fiverr-private/homepage_utils';
import { getContext } from '@fiverr-private/fiverr_context';
import { mixpanel } from '@fiverr-private/obs';
import { TabbedCarouselTabClickData } from '@fiverr-private/personalized_content/src/PersonalizedContent/components/TabsView/types';
import { MetadataContext } from '../../context/metadata/metadata.context';
import { UGCContext } from '../../context/ugc/ugc.context';
import { PersonalizedData } from '../RowSections/components/PersonalizedRowSection/PersonalizedRowSection.types';
import { useUser } from '../../context/user/userProvider';
import { translateAndStoreUgc } from '../../shared/utils/machineTranslation/utils';
import { PAGE_NAME } from '../../../../configuration/constants';
import { appTranslate as translate } from '../../i18n';
import WithImpression from '../../../utils/reporting/bigQuery/impressions/components/withImpression';
import { SectionName } from '../../../lib/interfaces/tabbedCarouselsContent';
import { TabbedCarouselsProps, ShortTitlesSections } from './types';
import { eagerSectionMap, enrichTabbedCarouselData, isValidSectionNames } from './utils';

type TitleKey = `personalized_tabs.short_title.${ShortTitlesSections}` | `personalized_tabs.title.${SectionName}`;

const TabbedCarousels = (props: TabbedCarouselsProps): JSX.Element => {
    const sectionNames: SectionName[] = props.tabbedCarouselsContent?.sectionNames || [];
    const { layoutId, mobileView = false, withBackgroundColor = false, subtitle, subtitleComponent } = props;
    const { rollouts } = useContext(MetadataContext);
    const { locale } = getContext();
    const { userData, buyerInsights } = useUser();
    const buyerState = getBuyerState(buyerInsights?.segment);
    const { ugcMap, showTranslatedContentRef, addUgcData } = useContext(UGCContext);
    const localizedPersonalizedData = showTranslatedContentRef.current
        ? ugcMap.translated.personalizedContent
        : ugcMap.original.personalizedContent;
    const mixPanelEventPrefix = 'Tabbed Carousel';
    const componentName = 'tabbed_carousel';
    const [impressionId, setImpressionId] = useState<string>('');
    const firstSection = sectionNames[0];
    const resolvedSection =
        firstSection !== undefined && firstSection !== null
            ? eagerSectionMap[firstSection] ?? firstSection
            : 'warm_start';
    const titleKey: TitleKey = `personalized_tabs.title.${resolvedSection}`;
    const title = translate(titleKey);
    const tabbedCarouselData = localizedPersonalizedData?.sections
        ? enrichTabbedCarouselData(localizedPersonalizedData?.sections)
        : {};

    const onImpressionIdAssign = (impressionId: string) => {
        setImpressionId(impressionId);
    };

    const reportTabClick = (tabEnrichedData: TabbedCarouselTabClickData) => {
        const commonClickData: IClickData = {
            imp: {
                id: impressionId,
            },
            page_element_name: componentName,
        };
        const enrichedClickData = {
            ...commonClickData,
            layoutId,
            clicked_tab_data: { ...tabEnrichedData },
        };

        sendClickEvent(enrichedClickData);
        mixpanel.track(`Home - ${mixPanelEventPrefix} Click`, {
            ...tabEnrichedData,
            ...getMixPanelCommonProperties({ layoutId, buyerState }),
        });
    };

    return isValidSectionNames(sectionNames) ? (
        <WithImpression
            onImpressionIdAssign={onImpressionIdAssign}
            mixPanelEventPrefix={mixPanelEventPrefix}
            intersectionThreshold={0.7}
            biEnrichment={{
                layoutId,
                buyerState,
                componentName,
                componentType: componentName,
                ...tabbedCarouselData,
            }}
        >
            <Box width="100%" data-testid="tabbed-carousels-wrapper">
                <TabsView
                    title={title}
                    subtitle={subtitle}
                    subtitleComponent={subtitleComponent}
                    sectionNames={sectionNames}
                    personalizedContent={localizedPersonalizedData?.sections}
                    recommendationsParams={{
                        funnelName: PAGE_NAME,
                        pageName: PAGE_NAME,
                    }}
                    onCardImpression={onCardImpression}
                    appName={PAGE_NAME}
                    perseusName={PAGE_NAME}
                    shouldFetchCollections
                    rollouts={rollouts}
                    reportTabClick={reportTabClick}
                    showTranslatedUGC={showTranslatedContentRef.current}
                    onFetchSucceeded={async (data?: PersonalizedData) => {
                        translateAndStoreUgc({
                            originalPersonalizedContent: data,
                            targetLocale: locale,
                            userData,
                            addUgcData,
                            grafanaKey: `machine.translate.ugc.tabbed_carousels.${locale}`,
                        });
                    }}
                    mobileView={mobileView}
                    withBackgroundColor={withBackgroundColor}
                />
            </Box>
        </WithImpression>
    ) : (
        <></>
    );
};

export default TabbedCarousels;
